import { Children, cloneElement, createContext, memo, useContext, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import _ from 'lodash';
import clsx from 'clsx';
import { useTheme } from '@mui/styles';
import useSyncScroll from './useSyncScroll';
import { validateScrollColor } from '../../utils/colors';

const initialLetters = (text = '') => {
  if (typeof text !== 'string') return '';
  return text
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const stickyRightCommonStyled = 'md:sticky md:right-0 md:border-l-1';
const stickyLeftCommonStyled = 'md:sticky md:left-0 md:border-r-1';

const stickyRightHeadStyled = 'md:border-l-neutral-700 md:shadow-md z-20';
const stickyLeftHeadStyled = 'md:border-r-neutral-700 md:shadow-md z-20';

const stickyRightRowStyled = 'md:border-l-neutral-400 md:shadow-md z-10';
const stickyLeftRowStyled = 'md:border-r-neutral-400 md:shadow-md z-10';

const getScrollbarStyled = (scrollColor) => ({
  '&::-webkit-scrollbar': { height: '8px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)' },
  scrollbarWidth: 'thin',
  scrollbarColor: scrollColor ? `${scrollColor} rgba(0,0,0,0.05)` : undefined,
});

const TableContext = createContext({});
const { Provider } = TableContext;

const WithProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return <Provider value={{ isCollapsed, setIsCollapsed }}>{children}</Provider>;
};

const Collapsed = memo(({ nestedData }) => {
  const { isCollapsed } = useContext(TableContext);

  const metadata = useMemo(() => {
    if (!nestedData) return [];
    return Object.entries(nestedData).map(([key, value]) => ({
      keyObj: key,
      valueObj: value,
    }));
  }, [nestedData]);

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            {metadata.length > 0 ? (
              <Table size="small" aria-label="metadata">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '100%' }}>Clave</TableCell>
                    <TableCell sx={{ width: '100%' }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metadata.map((rowItem, index) => (
                    <TableRow key={`nestedtable_${rowItem.keyObj}_${index}`}>
                      <TableCell component="th" scope="row">
                        {rowItem.keyObj}
                      </TableCell>
                      <TableCell>{rowItem.valueObj}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-auto w-full py-16 flex-col justify-center items-center">
                <Typography variant="subtitle1" gutterBottom>
                  No hay datos disponibles
                </Typography>
              </div>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    height: '4rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
}));

/**
 * Componente de tabla compuesta con soporte para filas anidadas, carga y estados vacíos.
 * Incluye scroll horizontal visible tanto en la parte superior como en la inferior.
 *
 * @component
 * @param {boolean} [selectable=false] - Determina si las filas son seleccionables.
 * @param {Function} [onRowClick=() => {}] - Función llamada al hacer clic en una fila, recibe el índice y el item.
 * @param {React.ReactNode} children - Columnas a renderizar como hijos del componente.
 * @param {Array<Object>} headers - Encabezados de la tabla, con propiedades como `title`, `width`, `align`, etc.
 * @param {Array<Object>} data - Datos a renderizar en las filas.
 * @param {Function} [metadata=() => {}] - Función opcional para obtener metadata asociada a una fila.
 * @param {boolean} isLoading - Si está cargando los datos.
 * @param {'small'|'medium'} [size='small'] - Tamaño de la tabla.
 * @param {string} [headerBgColor] - Color de fondo para el encabezado.
 * @param {string} [headerTxtColor] - Color del texto en el encabezado.
 * @param {boolean} [enableForceScroll=false] - Habilita el scrollbar horizontal forzado
 * @param {string} [scrollColor='#2f4777'] - Color del thumb del scrollbar horizontal (top mirror y contenedor principal). Si no es válido, usa `#2f4777` y reporta error en consola.
 * @param {Object} props - Propiedades adicionales pasadas al contenedor de la tabla.
 */
export const ComposedTable = ({
  selectable = false,
  onRowClick = () => {},
  children,
  headers = [],
  data,
  metadata = () => {},
  isLoading,
  size = 'small',
  headerBgColor,
  headerTxtColor,
  sx,
  enableForceScroll = false,
  scrollColor,
  ...props
}) => {
  const _thme = useTheme();

  const { topScrollRef, tableContainerRef, innerRef } = useSyncScroll({
    enabled: enableForceScroll,
  });

  const scrollStylesDeterminator = useMemo(() => {
    const entryColor = typeof scrollColor === 'function' ? scrollColor(_thme) : scrollColor;
    const scrollColorValidation = validateScrollColor(entryColor);
    const safeScrollColor = scrollColorValidation.isValid ? scrollColorValidation.value : undefined;
    return getScrollbarStyled(safeScrollColor);
  }, [scrollColor, getScrollbarStyled, validateScrollColor, _thme]);

  const memoizedChildren = useMemo(
    () => Children.map(children, (child) => cloneElement(child)),
    [children]
  );

  const memoizedRows = useMemo(() => {
    return data.map((item, indexRow) => (
      <WithProvider key={indexRow}>
        <StyledTableRow
          onClick={() => onRowClick(indexRow, item)}
          sx={{ cursor: selectable ? 'pointer' : undefined }}
        >
          {Children.map(memoizedChildren, (child) => cloneElement(child, { item }))}
        </StyledTableRow>
        <Collapsed nestedData={_.has(item, 'metadata') ? item.metadata : undefined} />
      </WithProvider>
    ));
  }, [data, memoizedChildren, onRowClick, selectable]);

  return useMemo(() => {
    return (
      <Paper
        sx={{
          borderRadius: '0.4rem',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...sx,
        }}
        {...props}
      >
        {enableForceScroll && (
          <div
            ref={topScrollRef}
            style={{
              display: 'block',
              minHeight: '10px',
              position: 'relative',
              overflowX: 'scroll',
              overflowY: 'hidden',
              ...scrollStylesDeterminator,
            }}
          >
            <div style={{ minHeight: '4px', width: '100%' }} />
          </div>
        )}

        <TableContainer
          ref={tableContainerRef}
          component="div"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowX: enableForceScroll ? 'scroll' : 'auto',
            overflowY: 'auto',
            position: 'relative',
            ...(enableForceScroll && scrollStylesDeterminator),
          }}
        >
          <Table ref={innerRef} aria-label="composed table component" size={size} stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map(
                  (
                    { title, width, minWidth, maxWidth, fixedRight, fixedLeft, align = 'center' },
                    index
                  ) => (
                    <StyledTableCell
                      key={`${title}_${index}`}
                      align={align}
                      style={{
                        width,
                        minWidth,
                        maxWidth,
                        backgroundColor: headerBgColor,
                        color: headerTxtColor,
                      }}
                      className={clsx(
                        fixedRight &&
                          !fixedLeft &&
                          `${stickyRightCommonStyled} ${stickyRightHeadStyled}`,
                        !fixedRight &&
                          fixedLeft &&
                          `${stickyLeftCommonStyled} ${stickyLeftHeadStyled}`
                      )}
                    >
                      {title}
                    </StyledTableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            {data.length > 0 && <TableBody>{memoizedRows}</TableBody>}
            {!isLoading && data.length <= 0 && (
              <div
                className="bg-slate-950 w-full top-[3.4rem] bg-opacity-50 flex justify-center items-center pt-16 pb-16"
                style={{ position: 'absolute' }}
              >
                <div>
                  <Typography variant="body2">No hay datos que mostrar</Typography>
                </div>
              </div>
            )}
          </Table>

          {isLoading && (
            <div
              className="h-full w-full left-0 top-0 flex flex-auto justify-center items-center"
              style={{
                position: 'absolute',
                backgroundColor: 'rgba(56, 56, 56, 0.22)',
                backdropFilter: 'blur(1px)',
                zIndex: 100,
              }}
            >
              <CircularProgress sx={{ color: '#FEFEFE' }} size={60} />
            </div>
          )}
        </TableContainer>
      </Paper>
    );
  }, [
    data,
    memoizedRows,
    headers,
    isLoading,
    size,
    headerBgColor,
    headerTxtColor,
    props,
    enableForceScroll,
    scrollStylesDeterminator,
  ]);
};

const Column = memo(({ fixedRight, fixedLeft, content = () => {}, item }) => {
  const renderedContent = useMemo(() => {
    const rendered = content(item);
    if (typeof rendered === 'string' || typeof rendered === 'number') {
      return (
        <Typography
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 3,
            lineClamp: 3,
            wordBreak: 'break-all',
          }}
        >
          {rendered}
        </Typography>
      );
    }
    return rendered;
  }, [content, item]);

  return (
    <StyledTableCell
      align="center"
      sx={{ backgroundColor: (_theme) => _theme.palette.background.default }}
      className={clsx(
        fixedRight && !fixedLeft && `${stickyRightCommonStyled} ${stickyRightRowStyled}`,
        !fixedRight && fixedLeft && `${stickyLeftCommonStyled} ${stickyLeftRowStyled}`
      )}
    >
      {renderedContent}
    </StyledTableCell>
  );
});

const NestedButton = memo(() => {
  const { isCollapsed, setIsCollapsed } = useContext(TableContext);
  return (
    <IconButton aria-label="expand row" size="small" onClick={() => setIsCollapsed(!isCollapsed)}>
      {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  );
});

const Thumbail = memo(({ baseUri, path, alt, name, color, square, ...props }) => {
  const src = useMemo(() => `${baseUri ?? ''}${baseUri ? '/' : ''}${path}`, [baseUri, path]);
  return (
    <Avatar
      alt={alt}
      sx={{ bgcolor: color }}
      variant={square ? 'square' : undefined}
      src={src}
      {...props}
    >
      {name && initialLetters(name)}
    </Avatar>
  );
});

const Preview = memo(({ baseUri, path, alt, width, height, style }) => {
  const src = useMemo(() => `${baseUri ?? ''}${baseUri ? '/' : ''}${path}`, [baseUri, path]);
  if (!path) {
    return <HideImageOutlinedIcon style={{ width, height, ...style, color: '#a6a6a6' }} />;
  }
  return <img src={src} alt={alt} style={{ width, height, ...style }} />;
});

ComposedTable.Column = Column;
ComposedTable.NestedButton = memo(NestedButton);
ComposedTable.Thumbail = Thumbail;
ComposedTable.Preview = Preview;
